import React from 'react'
import { cn } from '../../lib/cn'
import { CifraAnimada } from './Cifras'

const TONOS = {
  social: {
    topBar: 'border-t-social-600',
    border: 'border-slate-200 hover:border-social-500',
    bg: 'bg-gradient-to-b from-social-50/60 via-white to-white',
    badge: 'bg-social-100 text-social-900 border-social-300',
    rotulo: 'text-social-950',
    defaultIcon: '👥',
  },
  emerald: {
    topBar: 'border-t-emerald-600',
    border: 'border-slate-200 hover:border-emerald-500',
    bg: 'bg-gradient-to-b from-emerald-50/60 via-white to-white',
    badge: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    rotulo: 'text-emerald-950',
    defaultIcon: '🍲',
  },
  sky: {
    topBar: 'border-t-sky-600',
    border: 'border-slate-200 hover:border-sky-500',
    bg: 'bg-gradient-to-b from-sky-50/60 via-white to-white',
    badge: 'bg-sky-100 text-sky-900 border-sky-300',
    rotulo: 'text-sky-950',
    defaultIcon: '📊',
  },
  teal: {
    topBar: 'border-t-teal-600',
    border: 'border-slate-200 hover:border-teal-500',
    bg: 'bg-gradient-to-b from-teal-50/60 via-white to-white',
    badge: 'bg-teal-100 text-teal-900 border-teal-300',
    rotulo: 'text-teal-950',
    defaultIcon: '🥪',
  },
  amber: {
    topBar: 'border-t-amber-500',
    border: 'border-slate-200 hover:border-amber-500',
    bg: 'bg-gradient-to-b from-amber-50/60 via-white to-white',
    badge: 'bg-amber-100 text-amber-950 border-amber-300',
    rotulo: 'text-amber-950',
    defaultIcon: '⚡',
  },
  rose: {
    topBar: 'border-t-rose-600',
    border: 'border-slate-200 hover:border-rose-500',
    bg: 'bg-gradient-to-b from-rose-50/60 via-white to-white',
    badge: 'bg-rose-100 text-rose-900 border-rose-300',
    rotulo: 'text-rose-950',
    defaultIcon: '⚠️',
  },
  violet: {
    topBar: 'border-t-violet-600',
    border: 'border-slate-200 hover:border-violet-500',
    bg: 'bg-gradient-to-b from-violet-50/60 via-white to-white',
    badge: 'bg-violet-100 text-violet-900 border-violet-300',
    rotulo: 'text-violet-950',
    defaultIcon: '💰',
  },
  slate: {
    topBar: 'border-t-slate-500',
    border: 'border-slate-200 hover:border-slate-500',
    bg: 'bg-gradient-to-b from-slate-100/70 via-white to-white',
    badge: 'bg-slate-100 text-slate-800 border-slate-300',
    rotulo: 'text-slate-900',
    defaultIcon: '📌',
  },
}

function resolverIcono(titulo = '', tono = 'social', iconoManual = null) {
  if (iconoManual) return iconoManual
  const t = String(titulo || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  if (t.includes('inscript') || t.includes('padron') || t.includes('chico')) return '👥'
  if (t.includes('desayuno')) return '☕'
  if (t.includes('almuerzo')) return '🍲'
  if (t.includes('merienda')) return '🥪'
  if (t.includes('alerta') || t.includes('medica')) return '🩺'
  if (t.includes('guardar') || t.includes('grabado')) return '💾'
  if (t.includes('racion') || t.includes('servida')) return '🍽️'
  if (t.includes('mercaderia') || t.includes('remito')) return '📦'
  if (t.includes('gasto') || t.includes('fijo') || t.includes('servicio')) return '⚡'
  if (t.includes('costo total') || t.includes('total')) return '💰'
  if (t.includes('costo por') || t.includes('costo')) return '🏷️'
  if (t.includes('presente')) return '✅'
  if (t.includes('ausente')) return '❌'
  if (t.includes('registro')) return '📋'
  if (t.includes('hospital')) return '🏥'
  if (t.includes('social')) return '🤝'
  if (t.includes('expediente')) return '📂'

  return TONOS[tono]?.defaultIcon || '📊'
}

/**
 * Tarjeta de cifra destacada con banda superior de color, ícono, número en negrita y contexto.
 * @param {{ titulo: string, valor: number|string, sub?: string, tono?: keyof typeof TONOS,
 *           icono?: React.ReactNode, ayuda?: string, cargando?: boolean, formato?: (n:number)=>string,
 *           className?: string }} props
 */
export default function TarjetaCifra({ titulo, valor, sub, tono = 'social', icono, ayuda, cargando = false, formato, className }) {
  const t = TONOS[tono] || TONOS.social
  const iconoFinal = resolverIcono(titulo, tono, icono)
  const esNumero = typeof valor === 'number' && Number.isFinite(valor)
  const tam = esNumero || String(valor ?? '').length <= 8 ? 'text-3xl sm:text-4xl' : 'text-xl sm:text-2xl'

  const subTexto = String(sub || '').toLowerCase()
  const esAlertaPendiente = subTexto.includes('falta') || subTexto.includes('sin asistencia')
  const esTodoOk = subTexto.includes('todo grabado')

  return (
    <article
      title={ayuda}
      className={cn(
        'group relative flex flex-col justify-between overflow-hidden rounded-2xl border-2 bg-white p-3.5 sm:p-4 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md border-t-[5px]',
        t.border,
        t.topBar,
        t.bg,
        cargando && 'opacity-60',
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={cn('text-[11px] font-black uppercase tracking-wider leading-tight', t.rotulo)}>
          {titulo}
        </p>
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border text-sm font-bold shadow-sm transition-transform duration-200 group-hover:scale-110', t.badge)}>
          {iconoFinal}
        </span>
      </div>

      <div className="mt-2.5">
        <p className={cn('font-black leading-none tabular-nums tracking-tight text-slate-900', tam)}>
          {esNumero ? <CifraAnimada valor={valor} formato={formato} /> : valor}
        </p>

        {sub && (
          <div className="mt-2">
            {esAlertaPendiente ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-black text-amber-950 border border-amber-300">
                ⚠️ {sub}
              </span>
            ) : esTodoOk ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2 py-0.5 text-[11px] font-black text-emerald-950 border border-emerald-300">
                ✓ {sub}
              </span>
            ) : (
              <p className="text-xs font-bold text-slate-600 line-clamp-1">{sub}</p>
            )}
          </div>
        )}
      </div>
    </article>
  )
}
